import type { FC } from 'react';

import { formatBasecampRoomAccessCode } from '../utils/formatBasecampRoomAccessCode';

/**
 * Data needed to render a check-in slip. Sourced from the `selfCheckin`
 * mutation response.
 *
 * Slip content is intentionally limited to what a guest needs at the moment
 * of check-in: where their room is and how to get into it. Guest name, dates,
 * and Wi-Fi credentials are deliberately excluded — the guest already knows
 * who they are and when they're staying, and Wi-Fi details are provided in a
 * separate printed pamphlet.
 */
export interface CheckInSlipData {
  roomNumber: string;
  /** Required for raw staff codes; guest API codes are already formatted. */
  roomType?: string | null;
  buildingName: string | null;
  buildingAccessCode: string | null;
  roomAccessCode: string | null;
}

function escapeHTML(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function renderCheckInSlipHTML(data: CheckInSlipData): string {
  const room = escapeHTML(data.roomNumber);
  const building = data.buildingName ? escapeHTML(data.buildingName) : null;
  const buildingCode = data.buildingAccessCode
    ? escapeHTML(data.buildingAccessCode)
    : null;
  const formattedRoomCode = formatBasecampRoomAccessCode({
    roomNumber: data.roomNumber,
    roomType: data.roomType ?? null,
    currentAccessCode: data.roomAccessCode,
  });
  const roomCode = formattedRoomCode ? escapeHTML(formattedRoomCode) : null;
  const hasCodes = buildingCode !== null || roomCode !== null;

  // Sized for an Epson ET-4800 (A4/Letter inkjet). The slip is centered on
  // the page with generous margins; it does not attempt to fill the sheet.
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Check-in slip</title>
    <style>
      @page { size: A4; margin: 20mm; }
      body { font-family: -apple-system, system-ui, sans-serif; color: #000; margin: 0; padding: 0; }
      .sheet { max-width: 140mm; margin: 0 auto; text-align: center; }
      .label { font-size: 10pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.2em; color: #666; margin-bottom: 4pt; }
      .building-label { font-size: 10pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.2em; color: #666; margin: 0; }
      .building { font-size: 36pt; font-weight: 700; line-height: 1.1; margin: 4pt 0 0 0; }
      .room-label { font-size: 10pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.2em; color: #666; margin-top: 22pt; }
      .room { font-size: 96pt; font-weight: 800; line-height: 1; margin: 4pt 0 24pt 0; }
      .section { margin: 18pt 0; padding: 12pt 16pt; border: 1pt solid #ccc; border-radius: 4pt; }
      .section-title { font-size: 10pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.2em; color: #666; margin-bottom: 8pt; }
      .pair { display: flex; justify-content: space-between; align-items: baseline; padding: 4pt 0; font-size: 14pt; }
      .pair + .pair { border-top: 1pt dotted #ddd; }
      .pair .key { color: #666; font-size: 10pt; text-transform: uppercase; letter-spacing: 0.1em; }
      .pair .val { font-weight: 600; font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 16pt; }
    </style>
  </head>
  <body>
    <div class="sheet">
      ${
        building
          ? `<div class="building-label">Building</div>
      <div class="building">${building}</div>`
          : ''
      }
      <div class="room-label">Room</div>
      <div class="room">${room}</div>
      ${
        hasCodes
          ? `<div class="section">
        <div class="section-title">Access Codes</div>
        ${
          buildingCode
            ? `<div class="pair"><span class="key">Building</span><span class="val">${buildingCode}</span></div>`
            : ''
        }
        ${
          roomCode
            ? `<div class="pair"><span class="key">Room</span><span class="val">${roomCode}</span></div>`
            : ''
        }
      </div>`
          : ''
      }
    </div>
  </body>
</html>`;
}

export const CheckInSlipSummary: FC<{ data: CheckInSlipData }> = ({ data }) => {
  const hasCodes =
    data.buildingAccessCode !== null || data.roomAccessCode !== null;

  return (
    <div className="bc-fade-up flex w-full max-w-md flex-col items-center gap-6 px-6 text-center">
      {data.buildingName && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/30">
            Building
          </p>
          <div className="font-oswald mt-1 text-5xl font-bold leading-tight text-white">
            {data.buildingName}
          </div>
        </div>
      )}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/30">
          Room
        </p>
        <div className="font-oswald text-[120px] font-bold leading-none text-white">
          {data.roomNumber}
        </div>
      </div>

      {hasCodes && (
        <div className="w-full rounded-xl border border-white/[0.08] bg-white/[0.03] p-4">
          <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/30">
            Access Codes
          </p>
          <div className="grid grid-cols-2 gap-3">
            {data.buildingAccessCode && (
              <div className="text-center">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/30">
                  Building
                </p>
                <p className="font-oswald mt-1 text-2xl text-white">
                  {data.buildingAccessCode}
                </p>
              </div>
            )}
            {data.roomAccessCode && (
              <div className="text-center">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/30">
                  Room
                </p>
                <p className="font-oswald mt-1 text-2xl text-white">
                  {data.roomAccessCode}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
